/**
 * Utility helper to extract detailed error messages from API responses.
 * Backend error shape:
 * { success: false, error: { statusCode, error, message: string[] | string, path }, timestamp }
 */

function formatMessageValue(val: unknown): string | null {
  if (Array.isArray(val)) {
    const nonEmpties = val
      .map((item) => (typeof item === 'string' ? item.trim() : String(item ?? '').trim()))
      .filter((str) => str.length > 0)
    if (nonEmpties.length > 0) {
      return nonEmpties.join('; ').trim()
    }
    return null
  }
  if (typeof val === 'string' && val.trim().length > 0) {
    return val.trim()
  }
  return null
}

export function getMessageFromErrorData(
  data: unknown,
  fallback: string = 'Something went wrong'
): string {
  if (!data) {
    return fallback.trim()
  }

  if (typeof data === 'string') {
    const trimmed = data.trim()
    return trimmed.length > 0 ? trimmed : fallback.trim()
  }

  if (typeof data === 'object') {
    const record = data as Record<string, unknown>

    // a. data.error.message or data.error: if array, join; if string, use
    if (record.error && typeof record.error === 'object') {
      const errObj = record.error as Record<string, unknown>
      const formatted = formatMessageValue(errObj.message)
      if (formatted) {
        return formatted
      }
      if (typeof errObj.error === 'string' && errObj.error.trim().length > 0) {
        return errObj.error.trim()
      }
    } else if (typeof record.error === 'string' && record.error.trim().length > 0) {
      return record.error.trim()
    }

    // b. data.message: same handling (array or string).
    const formattedMessage = formatMessageValue(record.message)
    if (formattedMessage) {
      return formattedMessage
    }

    // c. data.errors (e.g. class-validator validation errors array or map)
    if (record.errors) {
      const formattedErrors = formatMessageValue(record.errors)
      if (formattedErrors) {
        return formattedErrors
      }
      if (typeof record.errors === 'object') {
        const errorValues = Object.values(record.errors as Record<string, unknown>)
        const formattedVals = formatMessageValue(errorValues)
        if (formattedVals) {
          return formattedVals
        }
      }
    }

    // d. data.detail or data.description
    if (typeof record.detail === 'string' && record.detail.trim().length > 0) {
      return record.detail.trim()
    }
    if (typeof record.description === 'string' && record.description.trim().length > 0) {
      return record.description.trim()
    }
  }

  return fallback.trim()
}

export function getApiErrorMessage(
  err: unknown,
  fallback: string = 'Something went wrong'
): string {
  if (!err) {
    return fallback.trim()
  }

  if (typeof err === 'string') {
    const trimmed = err.trim()
    return trimmed.length > 0 ? trimmed : fallback.trim()
  }

  if (typeof err === 'object') {
    const errObj = err as Record<string, unknown>

    // Check if response data exists
    if ('response' in errObj && errObj.response && typeof errObj.response === 'object') {
      const resp = errObj.response as Record<string, unknown>
      if ('data' in resp && resp.data !== undefined && resp.data !== null) {
        const extracted = getMessageFromErrorData(resp.data, '')
        if (extracted.length > 0) {
          return extracted
        }
      }
    } else if (
      // d. If there's no response (network error): "Network error. Please check your connection."
      ('isAxiosError' in errObj && Boolean(errObj.isAxiosError) && !errObj.response) ||
      ('request' in errObj && Boolean(errObj.request) && !errObj.response) ||
      errObj.code === 'ERR_NETWORK' ||
      errObj.code === 'ECONNABORTED' ||
      errObj.message === 'Network Error'
    ) {
      return 'Network error. Please check your connection.'
    }

    // e. err.message, then the fallback
    if (typeof errObj.message === 'string' && errObj.message.trim().length > 0) {
      return errObj.message.trim()
    }
  }

  return fallback.trim()
}

export function extractErrorMessage(
  err: unknown,
  fallback: string = 'An unexpected error occurred'
): string {
  return getApiErrorMessage(err, fallback)
}
